import { useSyncExternalStore } from 'react';
import { defaultSimFieldLabels, applyFieldLabels } from './fieldLabels';

let brandingVersion = 0;
const brandingListeners = new Set<() => void>();
const subscribeToBranding = (listener: () => void) => {
  brandingListeners.add(listener);
  return () => { brandingListeners.delete(listener); };
};
const getBrandingVersion = () => brandingVersion;

export function usePortalBrandingVersion() {
  return useSyncExternalStore(subscribeToBranding, getBrandingVersion, getBrandingVersion);
}

function notifyBrandingChanged() {
  brandingVersion += 1;
  brandingListeners.forEach((listener) => listener());
}

// Mirrors MapThemeSettings in the api's PortalOrchestrator; a semantic colour that expands to
// fewer variables here than there makes a previewed theme differ from the saved one
const themeVariableKeys = {
  primary: ['--button-primary-color', '--button-primary-border-color', '--top-nav-bg-color'],
  primarytext: ['--top-nav-text-color', '--button-text-color'],
  secondary: ['--offcanvas-bg-color', '--table-head-bg-color'],
  secondarytext: ['--offcanvas-text-color', '--table-head-text-color']
} as const;

type ThemeSettingsInput = Record<string, string> | null | undefined;
const FIELD_LABEL_SELECTOR_PREFIX = '--field-';

interface BrandingInput {
  themeSettings?: ThemeSettingsInput;
  fieldLabels?: Record<string, string>;
  supportedFieldKeys?: string[];
  logo?: string;
}

let appliedFieldKeys = Object.keys(defaultSimFieldLabels);
let appliedThemeVariableKeys = new Set<string>(Object.values(themeVariableKeys).flat());

function normalizeThemeSettings(themeSettings?: ThemeSettingsInput): Record<string, string> {
  if (!themeSettings) {
    return {};
  }

  const normalized: Record<string, string> = {};

  Object.entries(themeVariableKeys).forEach(([semanticKey, cssVariableKeys]) => {
    const semanticValue = themeSettings[semanticKey];

    cssVariableKeys.forEach((cssVariableKey) => {
      const value = semanticValue || themeSettings[cssVariableKey];

      if (value) {
        normalized[cssVariableKey] = value;
      }
    });
  });

  Object.entries(themeSettings).forEach(([key, value]) => {
    if (!key.startsWith('--') || key.startsWith(FIELD_LABEL_SELECTOR_PREFIX) || !value) {
      return;
    }

    normalized[key] = value;
  });

  return normalized;
}

export function clearThemeSettings() {
  if (typeof document === 'undefined') {
    return;
  }

  const root = document.documentElement;

  appliedThemeVariableKeys.forEach((cssVariableKey) => {
    root?.style.removeProperty(cssVariableKey);
  });
}

export function applyThemeSettings(themeSettings?: ThemeSettingsInput) {
  if (typeof document === 'undefined') {
    return;
  }

  const root = document.documentElement;
  const normalizedThemeSettings = normalizeThemeSettings(themeSettings);

  clearThemeSettings();

  Object.entries(normalizedThemeSettings).forEach(([cssVariableKey, value]) => {
    if (value) {
      root?.style.setProperty(cssVariableKey, value);
    }
  });

  appliedThemeVariableKeys = new Set(Object.keys(normalizedThemeSettings));
}

export function previewBranding(branding: BrandingInput) {
  const snapshot = typeof document !== 'undefined' ? document.documentElement.getAttribute('style') : null;

  applyPortalBranding(branding);

  return () => {
    if (typeof document === 'undefined') {
      return;
    }

    // ponytail: restoring the raw style attribute leaves appliedThemeVariableKeys/appliedFieldKeys
    // describing the preview rather than the restored theme; harmless as both key sets only ever union
    if (snapshot === null) {
      document.documentElement.removeAttribute('style');
      notifyBrandingChanged();
      return;
    }

    document.documentElement.setAttribute('style', snapshot);
    notifyBrandingChanged();
  };
}

export function applyPortalBranding(branding?: BrandingInput | null) {
  const nextFieldKeys = Array.from(
    new Set([...(branding?.supportedFieldKeys || []), ...Object.keys(branding?.fieldLabels || {}), ...appliedFieldKeys])
  );
  const root = typeof document !== 'undefined' ? document.documentElement : undefined;

  applyThemeSettings(branding?.themeSettings);
  if (branding?.logo) {
    root?.style.setProperty('--logo-src', `url("data:image/png;base64,${branding.logo}")`);
  } else {
    root?.style.removeProperty('--logo-src');
  }

  applyFieldLabels(branding?.fieldLabels || {}, nextFieldKeys);
  appliedFieldKeys = nextFieldKeys;
  notifyBrandingChanged();
}
